import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { useAppSelector } from '../stores/redux.store';

type Props = {
  children?: React.ReactNode;
};

const languages = {
  'pt-BR': () => import('../i18n/pt-BR'),
  'pt-PT': () => import('../i18n/pt-PT'),
  'zh-CN': () => import('../i18n/zh-CN'),
  'zh-TW': () => import('../i18n/zh-TW'),
  cs: () => import('../i18n/en'),
  da: () => import('../i18n/da'),
  de: () => import('../i18n/de'),
  en: () => import('../i18n/en'),
  es: () => import('../i18n/es'),
  et: () => import('../i18n/et'),
  fi: () => import('../i18n/fi'),
  fr: () => import('../i18n/fr'),
  hu: () => import('../i18n/hu'),
  it: () => import('../i18n/it'),
  ja: () => import('../i18n/ja'),
  ko: () => import('../i18n/ko'),
  nb: () => import('../i18n/nb'),
  nl: () => import('../i18n/nl'),
  pl: () => import('../i18n/pl'),
  ru: () => import('../i18n/ru'),
  sk: () => import('../i18n/sk'),
  sv: () => import('../i18n/sv'),
  th: () => import('../i18n/th'),
  tr: () => import('../i18n/tr'),
  uk: () => import('../i18n/uk'),
  vi: () => import('../i18n/vi'),
};

const fetchLanguage = async (locale: string) => {
  if (locale in languages) {
    const response = await languages[locale]();
    return response as { locale: string; messages: Record<string, string> };
  }

  throw new Error(`${locale} not found in languages config`);
};

const LocaleSwitcher = ({ children }: Props) => {
  const locale = useAppSelector(state => state.locale);
  const [fallbackLocale] = locale.split('-', 1);

  const [{ loading, messages, derivedLocale }, setState] = useState({
    loading: true,
    messages: {},
    derivedLocale: locale,
  });

  useEffect(() => {
    fetchLanguage(locale)
      .catch(() => fetchLanguage(fallbackLocale))
      .then(
        i18n => {
          setState({ loading: false, messages: i18n.messages, derivedLocale: i18n.locale });
        },
        () => {
          const content = `no translations were found for localeId ${locale} or fallbackLocale ${fallbackLocale}.`;
          console.debug(content);
          captureErrorMessage('i18n_fetch_language_error', { content, locale, fallbackLocale });
          setState(prevState => ({ ...prevState, loading: false }));
        }
      );
  }, [locale, fallbackLocale]);

  return (
    <IntlProvider locale={derivedLocale} messages={messages}>
      {loading ? <></> : children}
    </IntlProvider>
  );
};

export default LocaleSwitcher;
